import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
  deleteUpload,
  generatePresignedUrl,
  getPlaybackUrl,
  listWorkoutAssets,
  uploadFileToPresignedUrl,
} from "../services/upload.service";
import { getFileExtension, WorkoutVideoAsset } from "../types/upload.type";

const Workouts: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assets, setAssets] = useState<WorkoutVideoAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [playerOpen, setPlayerOpen] = useState(false);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [playbackLoading, setPlaybackLoading] = useState(false);
  const [playingAsset, setPlayingAsset] = useState<WorkoutVideoAsset | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refreshAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const response = await listWorkoutAssets();
      setAssets(response.assets ?? []);
    } catch {
      setError("Failed to load uploaded videos.");
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  useEffect(() => {
    refreshAssets();
  }, [refreshAssets]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setSuccess(null);
    setError(null);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!selectedFile) {
      setError("Please select a video file.");
      return;
    }

    const extension = getFileExtension(selectedFile.name);
    if (!extension) {
      setError("Only .mp4, .webm, and .mov video files are supported.");
      return;
    }

    setUploading(true);
    setUploadProgress(15);

    try {
      const presigned = await generatePresignedUrl(
        { original_name: selectedFile.name },
        extension
      );

      setUploadProgress(40);
      await uploadFileToPresignedUrl(selectedFile, presigned.upload_url, extension);
      setUploadProgress(85);

      await refreshAssets();

      setUploadProgress(100);
      setSuccess("Video uploaded successfully.");
      setTitle("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Upload failed.";
      setError(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePlay = async (asset: WorkoutVideoAsset) => {
    setPlayingAsset(asset);
    setPlayerOpen(true);
    setPlaybackUrl(null);
    setPlaybackLoading(true);
    setError(null);

    try {
      const playback = await getPlaybackUrl(asset.file_path);
      setPlaybackUrl(playback.playback_url);
    } catch {
      setError("Failed to load playback URL. The video may no longer exist.");
      setPlayerOpen(false);
    } finally {
      setPlaybackLoading(false);
    }
  };

  const handleClosePlayer = () => {
    setPlayerOpen(false);
    setPlaybackUrl(null);
    setPlayingAsset(null);
  };

  const handleDelete = async (asset: WorkoutVideoAsset) => {
    if (!window.confirm(`Delete "${asset.original_name}"?`)) return;

    setDeletingId(asset.id);
    setError(null);

    try {
      await deleteUpload(asset.id);
      await refreshAssets();
      setSuccess("Video deleted.");
    } catch {
      setError("Failed to delete video.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Upload Videos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload workout demo videos (MP4, WebM, or MOV) to storage. Admins link them to catalog exercises
        from the Catalog Videos page.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box component="form" onSubmit={handleUpload}>
          <TextField
            label="Title (optional, not saved)"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Defaults to filename"
            sx={{ mb: 2 }}
            disabled={uploading}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
            hidden
            onChange={handleFileChange}
          />

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              Choose video
            </Button>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
              </Typography>
            )}
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            startIcon={
              uploading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CloudUploadIcon />
              )
            }
            sx={{ mt: 3 }}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Upload video"}
          </Button>
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Uploaded videos
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Filename</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Linked exercises</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingAssets ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : assets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No videos uploaded yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              assets.map((asset) => (
                <TableRow key={asset.id}>
                  <TableCell>{asset.original_name}</TableCell>
                  <TableCell>{new Date(asset.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    {asset.linked_catalog_ids?.length
                      ? asset.linked_catalog_ids.join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handlePlay(asset)}
                      title="Play"
                    >
                      <PlayArrowIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(asset)}
                      disabled={deletingId === asset.id}
                      title="Delete"
                    >
                      {deletingId === asset.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={playerOpen} onClose={handleClosePlayer} maxWidth="md" fullWidth>
        <DialogTitle>{playingAsset?.original_name ?? "Workout video"}</DialogTitle>
        <DialogContent>
          {playbackLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : playbackUrl ? (
            <Box
              component="video"
              src={playbackUrl}
              controls
              autoPlay
              sx={{ width: "100%", borderRadius: 1, bgcolor: "black" }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePlayer}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Workouts;
